// removeBgClient — the browser half of background removal (SYS-10, SYS-10b).
//
// THE one switch for every "Remove background" button in the app — Developments,
// Originals, Lens and the Collection Book (book/data/bgEngine.js reads it too).
// Since SYS-10b it points at bg-cutout: BiRefNet on Replicate
// (docs/runbooks/bg-cutout-birefnet.md). remove.bg is switched off, not removed.
//
// ROLLBACK (one line): set BG_FUNCTION to ROLLBACK_FUNCTION. The remove-bg
// function, its code and the REMOVEBG_API_KEY secret are kept for exactly that.
// tests/contract/bg-switch.test.mjs fails if anything else names remove-bg.
//
// The contract is the same for both functions: multipart `image_file` in, a PNG
// with real transparency out, `{ error: <one sentence a person can act on> }` on
// failure. The Error keeps `.status` (402 = empty balance) and, on 429, `.retryAfter`.
// Callers show that sentence in a toast; they never replace it.
import { supabase } from '../supabase';

export const ROLLBACK_FUNCTION = 'remove-bg';
export const BG_FUNCTION = 'bg-cutout';
export const REFINE_FG = true;          // David, 23.10: book STOP B, then SYS-10b STOP A for every button

const PROVIDER = { 'bg-cutout': 'birefnet', [ROLLBACK_FUNCTION]: 'removebg' };
export const SIGN_IN_MESSAGE = 'Please refresh the page (and sign in) to remove backgrounds.';

async function accessToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch (_) {
    return null;
  }
}

/**
 * One photo through the background function → { blob, provider, uncertainty }, or an Error
 * carrying the function's own sentence, `.status` and (429) `.retryAfter`.
 * `fn` exists for the book's engine switch only; every other caller uses BG_FUNCTION.
 * `fetchImpl` lets tests mock the network — no test ever reaches the paid service.
 */
export async function removeBackgroundDetailed(blob, { fetchImpl, signal, fn = BG_FUNCTION } = {}) {
  const token = await accessToken();
  if (!token) { const e = new Error(SIGN_IN_MESSAGE); e.status = 401; throw e; }
  const fd = new FormData();
  fd.append('image_file', blob, blob && blob.type === 'image/png' ? 'image.png' : 'image.jpg');
  if (fn === ROLLBACK_FUNCTION) fd.append('size', 'auto');
  else fd.append('refine_fg', REFINE_FG ? 'true' : 'false');
  const res = await (fetchImpl || fetch)(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${fn}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
    signal,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err?.error || `The background service answered ${res.status}. Try again in a minute.`);
    e.status = res.status;
    const ra = Number(res.headers.get('retry-after'));      // 429: how long the service asks us to wait (s)
    if (Number.isFinite(ra) && ra > 0) e.retryAfter = ra;
    throw e;
  }
  const raw = res.headers.get('x-uncertainty-score');
  const u = Number(raw);
  return {
    blob: await res.blob(),
    provider: res.headers.get('x-bg-provider') || PROVIDER[fn] || 'removebg',
    uncertainty: raw != null && Number.isFinite(u) ? u : null,
  };
}

/** The plain call every "Remove background" button uses → a Blob (PNG with transparency). */
export async function removeBackgroundViaEdge(blob, opts) {
  return (await removeBackgroundDetailed(blob, opts)).blob;
}
